/* ============================================================
   Freebird Stores — Cart drawer confirmation banner
   ============================================================
   Reveals the "Added to cart" banner in the cart drawer, and only
   when the drawer opened because something was ADDED.
   Figma node 453:22340, frame 1a. Ticket DEV-94.

   WHY THIS IS NOT A LIQUID CONDITIONAL. The same drawer markup
   serves a shopper who just added a boot and a shopper who clicked
   the cart icon. Liquid renders once and cannot tell them apart, so
   the distinction has to be made at the moment the drawer opens.

   WHY NO BASE FILE IS CLAIMED. `assets/cart-drawer.js` already
   listens for the cart event on `document` to decide whether to
   auto-open. This element listens for the same public, bubbling
   event and needs nothing from that file — no claim, no ledger
   entry against it, no merge cost. The technique and its limits:
   docs/solutions/theme-architecture/extend-a-base-component-through-its-public-method.md
   ============================================================ */

/* THE TWO CART EVENTS SHARE ONE NAME, and this is the whole
   difficulty of the file.

   `assets/events.js` sets `CartAddEvent.eventName = ThemeEvents.cartUpdate`
   and `CartUpdateEvent` dispatches `ThemeEvents.cartUpdate` too — both
   are the string 'cart:update'. So the event name alone cannot tell an
   add from a removal, and a listener that trusts it announces "Added to
   cart" when a shopper REMOVES something.

   `detail.data.source` is the only field that separates them, and the
   values are not intuitive:

     product-form-component  a real add — PDP and quick-add       SHOW
     quick-order-quantity    B2B quick-order qty change           no
     quick-order-remove-all  a REMOVAL, dispatched as a CartAddEvent   no
     cart-items-component    qty change or remove inside the cart no

   `quick-order-remove-all` is the reason this is an ALLOWLIST rather
   than "anything that is not cart-items-component". A denylist that
   misses a removal shows a banner that lies; an allowlist that misses
   an add shows no banner. Those failures are not equally bad, so the
   list is closed and new add sources must be added deliberately. */
const ADD_SOURCES = new Set(['product-form-component']);

/* Base's own `assets/quick-add.js` bails on this same flag — a failed
   add still dispatches, carrying didError. */
const CART_EVENT = 'cart:update';
const VISIBLE_MS = 5000;

const BANNER_TAG = 'freebird-cart-banner';

/* WHEN THE CURRENT CONFIRMATION STOPS BEING TRUE. 0 means "not showing".
   `performance.now()`, not `Date.now()`: a clock change mid-window would
   either hide the banner instantly or pin it open.

   MODULE SCOPE, NOT A FIELD, because the thing it survives is the element.
   A re-render can either overwrite this element's attributes in place or
   replace the element outright, and instance state is lost in the second
   case — which is the one the header comment below already describes.

   WHAT IT IS FOR. The server renders this element `hidden` unconditionally:
   it ships empty and Liquid cannot know whether the drawer was opened by an
   add. So any re-render sourced from the SERVER carries `hidden` and puts it
   back on a banner that is mid-confirmation. Measured on staging, one add:

     538ms  cart:update from freebird-cart-sync, no `sections` key
     539ms  base re-fetches the header section from the server
     659ms  the response lands
     664ms  hidden = true, on the same element, still connected

   The shopper added an item and saw no confirmation at all. It read as a
   flaky check for weeks because the reconcile only re-renders when it finds
   a divergence, so it lands inside the 5s window only sometimes — 1 run in
   8, then 0 in 10, which is why reproducing it by repetition went nowhere.

   NOT FIXED IN assets/freebird-cart-sync.js, deliberately. That file is the
   re-render this was caught with, not the only one that can happen; a repair
   there covers one caller and leaves the next one to rediscover this. The
   banner is the thing that knows whether it is mid-confirmation, so it is
   the thing that defends the state. */
let visibleUntil = 0;

class FreebirdCartBanner extends HTMLElement {
  /* `hidden` is observed so the element is TOLD when something else sets it.
     Nothing else here reacts to an attribute, and this list exists only for
     the restore in `attributeChangedCallback`. */
  static observedAttributes = ['hidden'];

  /** @type {number | undefined} */
  #timer;

  /* CACHED, NOT RESOLVED ON DEMAND. `closest('dialog')` returns null
     once the element is detached, so a `disconnectedCallback` that
     re-resolves it unbinds nothing and the handler leaks. The morph
     detaches this element on every cart change, so "once" is really
     "every time the shopper touches the cart".
     @type {HTMLDialogElement | null} */
  #dialog = null;

  connectedCallback() {
    this.#dialog = /** @type {HTMLDialogElement | null} */ (this.closest('dialog'));

    /* The drawer is a <dialog>. Closing it must clear the banner, or
       the next MANUAL open — cart icon, no add — shows a stale
       confirmation for something the shopper did minutes ago. `close`
       does not bubble, so it is bound on the dialog itself. That is
       also why this stays on the element rather than moving to module
       scope with the cart listener: a delegated version would need
       `document` with `capture: true`, and getting that wrong silently
       restores the stale-banner bug this handler exists to prevent. */
    this.#dialog?.addEventListener('close', this.hide);
  }

  disconnectedCallback() {
    this.#dialog?.removeEventListener('close', this.hide);
    this.#dialog = null;
    this.#clearTimer();
  }

  /** True while the banner is inside an open drawer. */
  get isLive() {
    return Boolean(this.#dialog?.open);
  }

  /**
   * Put back a reveal that something else took away.
   *
   * @param {string} name
   * @param {string | null} _old
   * @param {string | null} value
   */
  attributeChangedCallback(name, _old, value) {
    /* Only a reinstatement of `hidden` is interesting. `value === null` is
       this method watching `show()` do its job, and acting on it would be an
       immediate loop. */
    if (name !== 'hidden' || value === null) return;

    /* `hide()` zeroes the window BEFORE it sets the attribute, so the
       element's own, legitimate hide arrives here with nothing left to
       defend and falls out on this line. That ordering is the whole
       interlock — reverse it and the banner can never hide itself. */
    if (visibleUntil === 0 || performance.now() >= visibleUntil) return;

    /* Synchronous, not deferred. If a later step of the same re-render sets
       it again, this fires again and restores again — self-healing beats
       guessing how many frames to wait, and a deferred restore would show a
       confirmation that had already blinked out. */
    this.hidden = false;

    /* The timer belongs to whichever element is on the page. On an in-place
       overwrite this one still holds it and `#rearm` is a no-op reset; on a
       REPLACEMENT the old element's `disconnectedCallback` cleared it and
       this is what gives the new element the remainder of the window. */
    this.#rearm();
  }

  /** Hide this banner when the CURRENT window ends, not `VISIBLE_MS` from now. */
  #rearm() {
    this.#clearTimer();
    const remaining = visibleUntil - performance.now();
    if (remaining <= 0) return;
    this.#timer = window.setTimeout(this.hide, remaining);
  }

  show = () => {
    /* Reset rather than stack. Adding two items quickly fires twice;
       Figma draws one banner, so the second add restarts the clock on
       the banner already showing instead of queueing another. */
    this.#clearTimer();

    /* OPENED BEFORE THE ATTRIBUTE IS TOUCHED, which is the mirror of the
       ordering in `hide()`. A re-render can land between the two statements
       below; with the window already open, `attributeChangedCallback` puts
       the reveal back, and with it still closed the confirmation is lost in
       the gap it exists to close. */
    visibleUntil = performance.now() + VISIBLE_MS;

    /* Toggling `hidden` off and on again within the same frame does not
       re-announce a live region — the text has not changed, so there is
       nothing new for the screen reader to read. That is correct here:
       the message is identical, and repeating it on every add would be
       noise rather than information. */
    this.hidden = false;

    /* NOTHING SCROLLS AND NOTHING TAKES FOCUS, and both of those are
       decisions rather than omissions.

       This banner can be raised from the BOTTOM of a scrolled drawer by the
       cross-sell row, where it used to reveal off screen and hide again on
       the timer below — a confirmation nobody saw. It is now pinned to the
       top of the scrolling column instead (`position: sticky` in
       assets/freebird-cart-drawer.css), so it is visible wherever the
       shopper is without moving them.

       An earlier version called `scrollIntoView` here. It worked, and it was
       rejected in review: it dragged the shopper away from the cross-sell
       tile they had just clicked, and away from the "Added!" state on it.
       Do not reinstate it alongside the pin — the two solve the same problem
       and only one of them leaves the shopper where they were.

       Focus was rejected before that: a screen reader already gets this
       through `role="status"`, and focus would land on an element that hides
       ITSELF on the timer below, dropping to <body>. */
    this.#timer = window.setTimeout(this.hide, VISIBLE_MS);
  };

  hide = () => {
    /* CLOSE THE WINDOW FIRST. `this.hidden = true` below re-enters
       `attributeChangedCallback`, which restores the reveal while the window
       is open — so a `hide()` that zeroed this afterwards would undo itself
       and the banner would never go away. */
    visibleUntil = 0;
    this.#clearTimer();
    this.hidden = true;
  };

  #clearTimer() {
    if (this.#timer !== undefined) {
      window.clearTimeout(this.#timer);
      this.#timer = undefined;
    }
  }
}

/* NO `-component` SUFFIX, and it is load-bearing rather than a style
   choice. `assets/component.js` decides ref ownership with a string
   match — any tag name ending in `-component` becomes a ref boundary,
   registered or not. This element sits inside `cart-items-component`;
   named `<freebird-cart-banner-component>` it would capture that
   component's refs and break the cart silently and completely. See
   docs/solutions/theme-architecture/a-wrapper-element-can-silently-break-base-refs.md

   It extends HTMLElement rather than Horizon's `Component` for the
   same reason: a `Component` subclass is also a ref boundary. */
if (!customElements.get(BANNER_TAG)) {
  customElements.define(BANNER_TAG, FreebirdCartBanner);
}

/* ============================================================
   THE LISTENER LIVES AT MODULE SCOPE, NOT ON THE ELEMENT
   ============================================================
   Bound in `connectedCallback` this file did not work at all, and it
   failed differently on each of the two paths a shopper can take.

   NON-EMPTY CART. This module is a deferred <head> script, so its
   `document` listener registered before `assets/component-cart-items.js`
   bound its own. Both fire on the same dispatch, in that order: the
   banner unhid itself, then `morphSection` replaced the markup with
   server HTML that still carried `hidden`. The reveal was undone
   before the browser ever painted it.

   EMPTY CART — the commoner path, since the drawer auto-opens on add.
   The banner element did not exist yet. The morph that ran during the
   dispatch is what created it, so the element's own listener was
   registered a beat too late to have seen the event that created it.

   TWO MECHANISMS FIX BOTH. The listener is bound once here, to
   `document`, independent of any element's lifetime; and the reveal
   RE-QUERIES the element after the morph rather than closing over a
   reference that the morph has already thrown away.

   WHY TWO FRAMES, stated precisely — the obvious explanation is wrong
   and was written down wrongly once already. `morphSection` in
   `assets/section-renderer.js` is declared `async`, which reads like
   the morph lands later. It does not: the function body contains no
   `await`, and `morph()` in `assets/morph.js` is synchronous apart
   from one `queueMicrotask`. The replacement therefore happens INSIDE
   this same dispatch. The two frames are not waiting for an async
   morph to resolve — they are getting past the dispatch entirely, and
   letting the browser run its own focus-fixup rule, before anything
   reads or writes the resulting DOM. `assets/freebird-cart-focus.js`
   waits the same two frames for the same reason.

   AND NO SERVER-SIDE PLACEMENT HELPS. Rendering the banner into the
   empty branch of `snippets/header-actions.liquid` looks like it would
   fix the empty-cart path and does not. The two branches differ in tag
   name at the items wrapper — `<div class="cart-drawer__items">` versus
   `<scroll-hint class="cart-drawer__items">` — and `assets/morph.js`
   treats differing `tagName` as incompatible, so the first add replaces
   that whole subtree. Any banner rendered there is destroyed and
   recreated regardless. Only the re-query below survives it.
   ============================================================ */

/** @param {Event} event */
function onCartEvent(event) {
  const data = /** @type {any} */ (event).detail?.data;
  if (!data) return;
  if (data.didError) return;
  if (!ADD_SOURCES.has(data.source)) return;

  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      /* Re-queried, never captured. The element that existed when the
         event fired is not the element on the page now. */
      /* Duck-typed, not `instanceof`. The define() guard above means a
         second copy of this module leaves the registry pointing at the
         FIRST class, so elements on the page would not be instances of
         this one and an `instanceof` test would silently never fire. */
      const banner = /** @type {any} */ (document.querySelector(BANNER_TAG));
      if (!banner || typeof banner.show !== 'function') return;

      /* The drawer can be dismissed inside the two-frame window, and a
         banner revealed on a closed drawer is the stale confirmation
         the `close` handler exists to prevent — arriving by the other
         door. */
      if (!banner.isLive) return;

      banner.show();
    })
  );
}

/* `document`, not the element: pages rendered by the `{% else %}` branch
   of `snippets/header-actions.liquid` carry a plain cart link and no
   drawer at all, so there is nothing to bind to and the re-query above
   simply finds nothing. */
document.addEventListener(CART_EVENT, onCartEvent);
